import { z } from "zod";

import type { JsonObject } from "../types";

import { coerceToJsonObject } from "../json";

export const planRevisionRepoSelectionDispatchEnvelopeSchema = z
  .object({
    issue_description: z.string().optional(),
    issue_key: z.string(),
    issue_summary: z.string(),
    provider_id: z.string(),
    scope: z.enum(["story", "subtasks", "single_subtask"]),
    source_control_provider_id: z.string().nullable(),
    source_control_repository_allowlist: z.array(z.string()),
  })
  .strict();

export type PlanRevisionRepoSelectionDispatchEnvelope = z.infer<
  typeof planRevisionRepoSelectionDispatchEnvelopeSchema
>;

export const planRevisionRepoSelectionResultSchema = z
  .object({
    reasoning: z
      .string()
      .optional()
      .describe("Brief explanation of why these repositories were selected."),
    selected_branches: z
      .record(z.string(), z.string().min(1))
      .describe(
        "Map of selected repository slug to selected branch. Every selected repo must have one entry.",
      ),
    selected_reference_repos: z
      .array(z.string())
      .describe(
        "Subset of selected_repos that should be checked out for read-only context only.",
      ),
    selected_repos: z
      .array(z.string())
      .describe(
        'List of repository slugs (e.g. "owner/repo" or "repo") relevant to the task.',
      ),
    selected_write_repos: z
      .array(z.string())
      .describe("Subset of selected_repos that require code changes."),
  })
  .strict()
  .superRefine((data, ctx) => {
    const repoSet = new Set(data.selected_repos);
    const branchRepoSet = new Set(Object.keys(data.selected_branches));
    const referenceRepoSet = new Set(data.selected_reference_repos);

    const reposMissingBranches = data.selected_repos.filter(
      (repo) => !branchRepoSet.has(repo),
    );
    const extraBranches = [...branchRepoSet].filter(
      (repo) => !repoSet.has(repo),
    );

    if (reposMissingBranches.length > 0) {
      ctx.addIssue({
        code: "custom",
        message: `Missing branches for: ${reposMissingBranches.join(", ")}`,
        path: ["selected_branches"],
      });
    }

    if (extraBranches.length > 0) {
      ctx.addIssue({
        code: "custom",
        message: `Branches provided for unselected repos: ${extraBranches.join(", ")}`,
        path: ["selected_branches"],
      });
    }

    const overlap = data.selected_write_repos.filter((repo) =>
      referenceRepoSet.has(repo),
    );
    if (overlap.length > 0) {
      ctx.addIssue({
        code: "custom",
        message: `Repositories cannot be both write and reference selections: ${overlap.join(", ")}`,
        path: ["selected_write_repos"],
      });
    }

    const writeOrReference = new Set([
      ...data.selected_write_repos,
      ...data.selected_reference_repos,
    ]);
    const missingClassification = data.selected_repos.filter(
      (repo) => !writeOrReference.has(repo),
    );
    if (missingClassification.length > 0) {
      ctx.addIssue({
        code: "custom",
        message: `Every selected repo must be classified as write or reference: ${missingClassification.join(", ")}`,
        path: ["selected_repos"],
      });
    }

    const unselectedWriteRepos = data.selected_write_repos.filter(
      (repo) => !repoSet.has(repo),
    );
    if (unselectedWriteRepos.length > 0) {
      ctx.addIssue({
        code: "custom",
        message: `Write repos must also appear in selected_repos: ${unselectedWriteRepos.join(", ")}`,
        path: ["selected_write_repos"],
      });
    }

    const unselectedReferenceRepos = data.selected_reference_repos.filter(
      (repo) => !repoSet.has(repo),
    );
    if (unselectedReferenceRepos.length > 0) {
      ctx.addIssue({
        code: "custom",
        message: `Reference repos must also appear in selected_repos: ${unselectedReferenceRepos.join(", ")}`,
        path: ["selected_reference_repos"],
      });
    }
  });

const planRevisionRepoSelectionHistoricalResultSchema = z
  .object({
    reasoning: z.string().optional(),
    selected_branches: z.record(z.string(), z.string()),
    selected_reference_repos: z.array(z.string()).default([]),
    selected_repos: z.array(z.string()),
    selected_write_repos: z.array(z.string()).default([]),
  })
  .strict();

export type PlanRevisionRepoSelectionResult = z.infer<
  typeof planRevisionRepoSelectionResultSchema
>;

export const planRevisionRepoSelectionCompletionEnvelopeSchema = z
  .object({ parsed_result: planRevisionRepoSelectionResultSchema.nullable() })
  .strict();

const planRevisionRepoSelectionHistoricalCompletionEnvelopeSchema = z
  .object({
    parsed_result: planRevisionRepoSelectionHistoricalResultSchema.nullable(),
  })
  .strict();

export type PlanRevisionRepoSelectionCompletionEnvelope = z.infer<
  typeof planRevisionRepoSelectionCompletionEnvelopeSchema
>;

export function serializePlanRevisionRepoSelectionDispatchEnvelope(
  envelope: PlanRevisionRepoSelectionDispatchEnvelope,
): JsonObject {
  return coerceToJsonObject(
    planRevisionRepoSelectionDispatchEnvelopeSchema.parse(envelope),
  );
}

export function serializePlanRevisionRepoSelectionCompletionEnvelope(
  envelope: PlanRevisionRepoSelectionCompletionEnvelope,
): JsonObject {
  return coerceToJsonObject(
    planRevisionRepoSelectionCompletionEnvelopeSchema.parse(envelope),
  );
}

export function parsePlanRevisionRepoSelectionCompletionEnvelope(
  value: unknown,
): PlanRevisionRepoSelectionCompletionEnvelope | null {
  const parsed =
    planRevisionRepoSelectionHistoricalCompletionEnvelopeSchema.safeParse(
      value,
    );
  return parsed.success ? parsed.data : null;
}

export function extractPlanRevisionRepoSelectionResult(
  value: unknown,
): PlanRevisionRepoSelectionResult | null {
  const completionEnvelope =
    parsePlanRevisionRepoSelectionCompletionEnvelope(value);
  if (completionEnvelope) {
    return completionEnvelope.parsed_result;
  }

  const directResult =
    planRevisionRepoSelectionHistoricalResultSchema.safeParse(value);
  if (directResult.success) {
    return directResult.data;
  }

  if (typeof value !== "string") {
    return null;
  }

  try {
    const parsedJson: unknown = JSON.parse(value);
    const parsedResult =
      planRevisionRepoSelectionHistoricalResultSchema.safeParse(parsedJson);
    return parsedResult.success ? parsedResult.data : null;
  } catch {
    return null;
  }
}
